import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    const urlParams = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt' && value) {
        urlParams.append(key, value.toString());
      }
    });
    try {
      const response = await fetch('https://readdy.ai/api/form/d9osep0senbniolesl1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlParams.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '妖精漫画是否完全免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的！妖精漫画所有内容完全免费，无需注册即可阅读全部漫画。我们致力于为漫画爱好者提供优质的免费阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '支持哪些阅读方式？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '妖精漫画支持下拉式在线阅读，完美适配手机、平板和电脑端。您可以在任何设备上流畅阅读，无需下载任何APP。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们与各大平台同步更新，热门漫画通常24小时内上线最新话。连载中的作品保持每周稳定更新，已完结作品可一次性畅读全本。',
        },
      },
      {
        '@type': 'Question',
        name: '可以下载漫画离线阅读吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '目前妖精漫画专注于在线即时阅读体验，暂不支持离线下载。但我们持续优化加载速度，即使在网络一般的情况下也能流畅阅读。',
        },
      },
      {
        '@type': 'Question',
        name: '如何反馈问题或建议？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系表单提交反馈，或发送邮件至 contact@yaojingmanhua.com。我们的客服团队在工作日 9:00-18:00 提供在线支持。',
        },
      },
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于妖精漫画',
    description: '妖精漫画是一个免费的在线漫画阅读平台，主打奇幻、魔法、妖精题材漫画大全。',
    url: 'https://yaojingmanhua.com/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-2 font-heading">关于妖精漫画</h1>
            <p className="text-sm text-foreground-500 mt-2">了解我们的品牌故事与愿景</p>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
            <div>
              <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">品牌介绍</h2>
              <div className="text-sm text-foreground-600 leading-relaxed space-y-4">
                <p>
                  妖精漫画创立于2024年，是一个专注于奇幻、魔法、妖精题材的免费在线漫画阅读平台。我们的使命是让每一位漫画爱好者都能方便快捷地阅读到自己喜爱的作品。
                </p>
                <p>
                  平台汇聚了韩漫、日漫、国漫等多种类型的优质漫画资源，涵盖冒险热血、恋爱甜宠、奇幻魔法等多种题材。我们与多家漫画工作室和创作者建立了合作关系，持续为读者带来最新、最热的漫画作品。
                </p>
                <p>
                  妖精漫画采用先进的下拉式阅读技术，为读者提供如丝般顺滑的阅读体验。无论是在手机、平板还是电脑上，都能获得流畅舒适的阅读感受。
                </p>
                <p>
                  我们始终秉持"免费、优质、便捷"的理念，持续优化平台体验，丰富内容库。目前平台已收录超过5000部漫画作品，为超过200万活跃读者提供服务。
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="bg-background-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-primary-500 font-heading">5000+</div>
                  <div className="text-xs text-foreground-500 mt-1">漫画资源</div>
                </div>
                <div className="bg-background-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-accent-500 font-heading">200万+</div>
                  <div className="text-xs text-foreground-500 mt-1">活跃读者</div>
                </div>
                <div className="bg-background-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-secondary-500 font-heading">99.9%</div>
                  <div className="text-xs text-foreground-500 mt-1">服务稳定性</div>
                </div>
              </div>
            </div>

            <div>
              <div className="rounded-lg overflow-hidden aspect-video bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Cozy%20reading%20nook%20with%20warm%20lighting%2C%20a%20person%20reading%20manga%20on%20a%20tablet%20in%20a%20comfortable%20chair%2C%20surrounded%20by%20bookshelves%2C%20soft%20ambient%20light%2C%20warm%20wood%20tones%2C%20relaxing%20atmosphere%2C%20editorial%20photography%2C%20no%20text&width=800&height=450&seq=about-banner-01&orientation=landscape"
                  alt="妖精漫画阅读体验"
                  title="妖精漫画 - 舒适的在线阅读体验"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="mb-14">
            <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">读者口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: '漫画迷小王',
                  rating: 5,
                  text: '妖精漫画的阅读体验真的很好！下拉式阅读超级流畅，画质也很清晰，已经推荐给身边所有喜欢看漫画的朋友了。',
                },
                {
                  name: '追更达人',
                  rating: 5,
                  text: '更新速度非常快，基本和原平台同步。分类也很全，韩漫、日漫、国漫都有，想看的都能找到。',
                },
                {
                  name: '奇幻爱好者',
                  rating: 5,
                  text: '作为奇幻漫画的死忠粉，妖精漫画的奇幻分类真的太棒了！妖精题材的漫画特别多，终于找到一个专门的平台了。',
                },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg p-5">
                  <div className="flex text-accent-500 mb-2">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.text}</p>
                  <p className="text-xs text-foreground-500 font-medium">— {review.name}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">常见问题</h2>
            <div className="space-y-3 mb-10">
              {[
                { q: '妖精漫画是否完全免费？', a: '是的！妖精漫画所有内容完全免费，无需注册即可阅读全部漫画。我们致力于为漫画爱好者提供优质的免费阅读体验。' },
                { q: '支持哪些阅读方式？', a: '妖精漫画支持下拉式在线阅读，完美适配手机、平板和电脑端。您可以在任何设备上流畅阅读，无需下载任何APP。' },
                { q: '漫画更新频率如何？', a: '我们与各大平台同步更新，热门漫画通常24小时内上线最新话。连载中的作品保持每周稳定更新，已完结作品可一次性畅读全本。' },
                { q: '可以下载漫画离线阅读吗？', a: '目前妖精漫画专注于在线即时阅读体验，暂不支持离线下载。但我们持续优化加载速度，即使在网络一般的情况下也能流畅阅读。' },
              ].map((faq, idx) => (
                <details key={idx} className="bg-background-100 rounded-lg group">
                  <summary className="px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 flex items-center justify-between list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 transition-transform group-open:rotate-180"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>

          <div className="bg-background-100 rounded-lg p-6 md:p-8">
            <h2 className="text-xl font-bold text-foreground-900 mb-2 font-heading">联系我们</h2>
            <p className="text-sm text-foreground-500 mb-6">有任何问题或建议？欢迎通过以下方式联系我们。</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">contact@yaojingmanhua.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                    <i className="ri-customer-service-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">在线客服</h4>
                    <p className="text-sm text-foreground-600">工作日 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">公司地址</h4>
                    <p className="text-sm text-foreground-600">中国上海市浦东新区张江高科技园区</p>
                  </div>
                </div>
              </div>

              <div>
                <form onSubmit={handleContactSubmit} className="space-y-4" data-readdy-form>
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（不超过500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    className="website-alt-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button type="submit" className="btn-primary w-full text-sm cursor-pointer">
                    <i className="ri-send-plane-line mr-2"></i>
                    发送留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-secondary-500 text-center">感谢您的留言，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}